/**
 * useAutoSave - Hook for debounced auto-save functionality.
 *
 * Provides a consistent pattern for saving action changes with:
 * - Debounced saves to reduce API calls
 * - Save status tracking for UI feedback
 * - Error handling with optional callback
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAutoSaveOptions {
  /** Debounce delay in milliseconds (default: 1500ms) */
  debounceMs?: number;
  /** Callback when save fails */
  onError?: (error: Error) => void;
  /** Callback when save succeeds */
  onSuccess?: () => void;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /**
   * Skip the very first `save()` call (default: true).
   *
   * The default suits the effect-driven usage this hook was written for —
   * `useEffect(() => save(value), [value])` fires once on mount with the
   * unchanged initial value, which must not be persisted. Set to `false` when
   * `save()` is called imperatively from an event handler, where swallowing
   * the first call would silently drop the user's first edit.
   */
  skipFirstSave?: boolean;
  /**
   * Fire-time gate, consulted inside the debounce timer immediately before the
   * save runs. Return `false` to drop the armed save without touching
   * `saveStatus` — used to suppress a timer that was armed while saving was
   * still allowed but fires after it no longer is (see the autosave freeze in
   * `useAutoSaveCoordination`). Not consulted by `saveNow`, which is the
   * explicit flush path.
   */
  shouldSave?: () => boolean;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveReturn<T> {
  /** Trigger a save with the given value */
  save: (value: T) => void;
  /** Force an immediate save (bypasses debounce) */
  saveNow: (value: T) => Promise<void>;
  /**
   * Cancel an armed debounce without saving. Clears the pending timer and
   * value so a save armed before some external state change (e.g. adopting a
   * server value) cannot fire afterwards.
   */
  cancelPendingSave: () => void;
  /** Current save status */
  saveStatus: SaveStatus;
  /** Whether a save is in progress */
  isSaving: boolean;
  /** Reset status to idle */
  resetStatus: () => void;
}

/**
 * Hook for debounced auto-save functionality.
 *
 * @example
 * ```tsx
 * const { save, saveStatus } = useAutoSave(
 *   async (content) => {
 *     await api.updateAction(actionId, { payload: content });
 *   },
 *   { debounceMs: 2000 }
 * );
 *
 * // Call save whenever content changes
 * useEffect(() => {
 *   save(editedContent);
 * }, [editedContent, save]);
 *
 * // Show status in UI
 * {saveStatus === 'saving' && <span>Saving...</span>}
 * {saveStatus === 'saved' && <span>Saved</span>}
 * ```
 */
export function useAutoSave<T>(
  onSave: (value: T) => Promise<void>,
  options: UseAutoSaveOptions = {}
): UseAutoSaveReturn<T> {
  const {
    debounceMs = 1500,
    onError,
    onSuccess,
    enabled = true,
    skipFirstSave = true,
    shouldSave,
  } = options;

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isFirstRenderRef = useRef(true);

  // Latest-ref so the armed timer always consults the current gate without
  // re-arming whenever the caller's callback identity changes.
  const shouldSaveRef = useRef(shouldSave);
  shouldSaveRef.current = shouldSave;

  // Track pending value for debounced save
  const pendingValueRef = useRef<T | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (statusResetTimeoutRef.current) {
        clearTimeout(statusResetTimeoutRef.current);
      }
    };
  }, []);

  // Execute the actual save
  const executeSave = useCallback(
    async (value: T) => {
      setSaveStatus('saving');
      try {
        await onSave(value);
        setSaveStatus('saved');
        onSuccess?.();
        // Clear any existing status reset timeout
        if (statusResetTimeoutRef.current) {
          clearTimeout(statusResetTimeoutRef.current);
        }
        // Reset to idle after showing "saved" briefly
        statusResetTimeoutRef.current = setTimeout(
          () => setSaveStatus('idle'),
          2000
        );
      } catch (error) {
        setSaveStatus('error');
        onError?.(error as Error);
      }
    },
    [onSave, onError, onSuccess]
  );

  // Debounced save
  const save = useCallback(
    (value: T) => {
      // Skip on first render (effect-driven callers only — see skipFirstSave)
      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        if (skipFirstSave) {
          return;
        }
      }

      if (!enabled) {
        return;
      }

      // Store pending value
      pendingValueRef.current = value;

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        if (pendingValueRef.current === null) {
          return;
        }
        const armedValue = pendingValueRef.current;
        pendingValueRef.current = null;
        if (shouldSaveRef.current && !shouldSaveRef.current()) {
          return;
        }
        executeSave(armedValue);
      }, debounceMs);
    },
    [enabled, debounceMs, executeSave, skipFirstSave]
  );

  // Immediate save (bypasses debounce)
  const saveNow = useCallback(
    async (value: T) => {
      // Clear pending debounce
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingValueRef.current = null;

      await executeSave(value);
    },
    [executeSave]
  );

  // Cancel an armed debounce without saving
  const cancelPendingSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingValueRef.current = null;
  }, []);

  // Reset status
  const resetStatus = useCallback(() => {
    setSaveStatus('idle');
  }, []);

  return {
    save,
    saveNow,
    cancelPendingSave,
    saveStatus,
    isSaving: saveStatus === 'saving',
    resetStatus,
  };
}
